import {GameStateEnums} from '../../../../../shared/GameEnums'
import {LobbyImage} from '@colonist/types'
import {UIUtilsChat} from '../../ui_chat/UIUtilsChat'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {UIGameFeedView} from './UIGameFeedView'
import {GameChatDevCardHelp} from '../GameChatDevCardHelp'
import {UIChatPost} from '../../ui_chat/UIChatPost'
import {UIAjaxTournamentResult} from '../../network/UIAjaxTournamentResult'
import {CA} from '../../analytics/Analytics'
import {UIMutePlayer} from '../../ui_player_options_popup/UIMutePlayer'
import {ChatData} from '@colonist/shared-types'
import {setElementInnerHTML} from '../../ui_utils/DomUtils'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {CardEnum, GameChatState, GameLogState} from '@colonist/game-states'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {ArrayUtils, hasSuspiciousWords} from '@colonist/utils'
import {convertGameLogDataToChatData} from '../../ui_utils/GameLogUtils'
import {IndexedGameLogData} from '@colonist/game-state-types'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {convertGameChatDataToChatData} from '../../ui_utils/GameChatUtils'
import {UIReplayController} from '../../ui_replay/UIReplayController'
import {UIReplayView} from '../../ui_replay/elements/UIReplayView'

export abstract class UIGameFeedController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected view: UIGameFeedView
    protected preventChatScrollUpdate: boolean
    protected preventLogScrollUpdate: boolean
    private preventClearingChatMessage: boolean
    private readonly devCardHelpController: GameChatDevCardHelp
    private readonly gameLogIndexToElement: Map<number, HTMLElement> = new Map<number, HTMLElement>()
    private gameChatElements: HTMLElement[] = []
    private gameLogClickListener: ((gameLogIndex: number) => void)[]

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.devCardHelpController = new GameChatDevCardHelp(uiGameManager)
        this.gameLogClickListener = []
        this.preventClearingChatMessage = false
    }

    registerGameLogClickListener(gameLogClickListener: (gameLogIndex: number) => void) {
        this.gameLogClickListener.push(gameLogClickListener)
    }

    clearGameLogClickListeners() {
        this.gameLogClickListener = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.CanvasResized: this.resizeChatBox(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            case UIGameEventEnums.NewTradeOfferReceived: this.hideHelpPopup(); break
            default: break
        }
    }

    writeToChat(chatData: ChatData) {
        if(UIGameFeedController.isMutedUser(chatData.username)) return

        this.uiGameManager.eventController.sendEvent(chatData, UIGameEventEnums.NewChatMessageReceived)

        const makeSound = this.view.isChatEnabled
        const postElement = UIChatPost.post(this.view.gameChat.container, chatData, this.preventChatScrollUpdate, makeSound, (type: CardEnum) => {
            this.showHelpPopup(type)
        })

        this.gameChatElements.push(postElement)
    }

    private static isMutedUser(username?: string): boolean {
        if(username == undefined) return false
        return UIMutePlayer.mutedPlayers.includes(username)
    }

    writeToLog(indexedGameLogData: IndexedGameLogData) {
        const chatData = convertGameLogDataToChatData(indexedGameLogData.gameLogData)
        const postElement = UIChatPost.post(this.view.gameLog.container, chatData, this.preventLogScrollUpdate, false,
            (type: CardEnum) => {
                this.showHelpPopup(type)
            })
        postElement.onclick = () => {
            this.clickedGameLog(indexedGameLogData.index)
        }
        this.gameLogIndexToElement.set(indexedGameLogData.index, postElement)
    }

    private clickedGameLog(gameLogIndex: number) {
        for(const gameLogClickListener of this.gameLogClickListener) {
            gameLogClickListener(gameLogIndex)
        }
    }

    private showHelpPopup(type: CardEnum) {
        const referenceElement = this.view.isChatEnabled ? this.view.gameChat : this.view.gameLog
        this.devCardHelpController.showHelpPopup(type, this.view.isChatEnabled, referenceElement.mainBlock.getBoundingClientRect())
    }

    private hideHelpPopup() {
        if(this.view.isChatEnabled) return
        this.devCardHelpController.disablePopup()
    }

    protected resizeChatBox() {
        const appW = parseFloat(this.uiGameManager.canvasController.app.view.style.width)
        const appH = parseFloat(this.uiGameManager.canvasController.app.view.style.height)
        this.view.resizeChatBox(appW, appH)
    }

    getLogContainerHeight(): number {
        const zoom = UIUtilsEjsVariables.isUserOnDiscord ? .75 : 1
        return this.view.gameLog.mainBlock.offsetHeight * zoom
    }

    getLogContainerWidth(): number {
        const zoom = UIUtilsEjsVariables.isUserOnDiscord ? .75 : 1
        return this.view.gameLog.mainBlock.offsetWidth * zoom
    }

    getLogContainer(): HTMLDivElement {
        return this.view.gameLog.mainBlock
    }

    setChatContainerVisibility(visible: boolean) {
        this.view.gameChat.setVisibility(visible)
    }

    setLogContainerVisibility(visible: boolean) {
        this.view.gameLog.setVisibility(visible)
    }

    protected sendMessageToServer() {
        const sentMessage = this.view.gameChat.inputElement.value
        if(sentMessage == '') return

        if(UIAjaxTournamentResult.sendTournamentData(sentMessage, this.uiGameManager.gameState, this.uiGameManager.gameController)) return
        this.uiGameManager.socketGameSend.submitChat(sentMessage)
        const debugMessage = UIUtilsChat.getDebugClientText(sentMessage)
        if(debugMessage != undefined) this.writeToChat({text: debugMessage, icon: LobbyImage.Admin})
        this.view.gameChat.inputElement.value = ''

        CA.sendEventSummed(CA.Category.Game, CA.Label.GameChatMessagesSent, UIGameEventEnums.GameEnd)

        if(!hasSuspiciousWords(sentMessage)) return
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.UserSentSuspiciousMessage,
            category: AnalyticEventCategory.Chat,
            stringValue: sentMessage,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private onBeginnerModeStart() {
        this.view.gameLog.container.replaceChildren()
    }

    clearEntireGameLog() {
        this.registerUIGameMechanic()
        setElementInnerHTML(this.view.gameLog.container, {key: 'strings:colonistCommon.utils.emptyString'})
        this.gameLogIndexToElement.clear()
    }

    buildGameLogs(gameLogState: GameLogState) {
        // remove child to more efficiently add a large number of HTML elements, then append again at the end.
        // Needed when re-loading a long game with a lot of logs
        this.view.gameLog.mainBlock.removeChild(this.view.gameLog.container)

        this.clearEntireGameLog()
        for(const gameLogIndexKey in gameLogState) {
            const gameLogIndex = Number(gameLogIndexKey)
            const gameLogData = gameLogState[gameLogIndexKey]

            this.writeToLog({
                index: gameLogIndex,
                gameLogData: gameLogData,
            })
        }
        this.view.gameLog.mainBlock.appendChild(this.view.gameLog.container)
        this.view.gameLog.container.scrollTop = this.view.gameLog.container.scrollHeight
    }

    buildGameChat(gameChatState: GameChatState) {
        this.registerUIGameMechanic()
        this.clearEntireGameChat()
        for(const gameChatIndexKey in gameChatState) {
            const gameChatIndex = Number(gameChatIndexKey)
            const gameChatData = gameChatState[gameChatIndex]

            const chatData = convertGameChatDataToChatData(gameChatData.text, this.uiGameManager)

            this.writeToChat(chatData)
        }
    }

    clearEntireGameChat() {
        if(this.preventClearingChatMessage) return
        setElementInnerHTML(this.view.gameChat.container, {key: 'strings:colonistCommon.utils.emptyString'})
        this.gameChatElements = []
    }

    clearGameChatWithIndexGreaterThanOrEqualTo(index: number) {
        if(this.preventClearingChatMessage) return
        const maxIndex = this.gameChatElements.length - 1
        if(index > maxIndex) return

        for(let gameChatIndex = maxIndex; gameChatIndex >= index; gameChatIndex--) {
            const gameChatElement = this.gameChatElements[gameChatIndex]
            gameChatElement.remove()
            ArrayUtils.removeElementFromArray(this.gameChatElements, gameChatElement)
        }
    }

    preventClearingGameChat() {
        this.preventClearingChatMessage = true
    }

    registerUIGameMechanic() {
        if(this.uiGameManager.eventController.isAlreadyRegistered(this)) return
        this.uiGameManager.eventController.registerUIGameMechanic(this)
    }

    scrollToGameLogIndex(gameLogIndex: number, bahavior: ScrollBehavior) {
        if(gameLogIndex == 0) {
            this.view.gameLog.container.scrollTo({top: 0, behavior: bahavior})
            return
        }

        const gameLogElement = this.gameLogIndexToElement.get(gameLogIndex)
        if(gameLogElement == undefined) return

        gameLogElement.scrollIntoView({block: 'center', behavior: bahavior})
    }

    fadeGameLogsWithIndexGreaterThan(gameLogIndex: number) {
        const pastGameLogOpacity = '1'
        const futureGameLogOpacity = '0.3'

        for(const [index, gameLogElement] of this.gameLogIndexToElement) {
            if(index <= gameLogIndex) gameLogElement.style.opacity = pastGameLogOpacity
            else gameLogElement.style.opacity = futureGameLogOpacity
        }
    }

    hideAllGameLogs() {
        for(const gameLogElement of this.gameLogIndexToElement.values()) {
            gameLogElement.hidden = true
        }
    }

    showGameLogWithIndex(index: number) {
        const gameLogElement = this.gameLogIndexToElement.get(index)
        if(gameLogElement == undefined) return
        gameLogElement.hidden = false
    }

    buildReplayView(replayController: UIReplayController) {
        this.view.buildReplayView(replayController)
    }

    setReplayViewVisible(visible: boolean) {
        if(this.view.replayView == undefined) return

        this.view.replayView.setVisibility(visible)
    }

    setReplayViewAction(actionIndex: number) {
        if(this.view.replayView == undefined) return

        this.view.replayView.updateActionIndex(actionIndex)
    }

    getReplayViewHeight(): number {
        if(this.view.replayView == undefined) return 0

        return this.view.replayView.mainBlock.clientHeight
    }

    getReplayView(): UIReplayView | undefined {
        return this.view.replayView
    }

    getReplayController(): UIReplayController | undefined {
        return this.view.replayView?.replayController
    }
}
